import { PublicProfileTypeEnum } from '@on-arte/core-types';
import { UseLogger, UseState, useLogger } from '@on-arte/ui';
import { useMemo, useState } from 'react';
import { QueryClient, UseQueryResult, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { FrontendApiError } from '@onArte/frontend/models';
import { UrlSearchParams } from '@onArte/frontend/types';
import { ssrQueryKey } from '@onArte/shared/constants';
import { PublicProfileList, PublicProfileListItem } from '@onArte/shared/interfaces';
import { normalizeAlphabetLetter } from '@onArte/shared/utils';

import { getPublicProfilesList } from '../api/requests';
import { QueryKey } from '../enums';
import { UsePublicProfilesListActions } from '../interfaces';

export const usePublicProfilesListActions: (type: PublicProfileTypeEnum) => UsePublicProfilesListActions = (
  type: PublicProfileTypeEnum
): UsePublicProfilesListActions => {
  const { logger }: UseLogger = useLogger();
  // Litera z adresu wchodzi jako stan POCZĄTKOWY, nie przez efekt: serwer sieje przedział
  // tej litery, a klucz zasiewu ją zawiera, więc obie strony muszą znać ją już przy
  // pierwszym renderowaniu. Widok i tak reaguje na późniejsze zmiany adresu.
  const [searchParams]: UrlSearchParams = useSearchParams();
  const [activeLetter, setActiveLetter]: UseState<string>
    = useState<string>(normalizeAlphabetLetter(searchParams.get('query')));
  const [alphabet, setAlphabet]: UseState<Record<string, number>> = useState<Record<string, number>>({});
  const [profilesList, setProfilesList]: UseState<PublicProfileListItem[]> = useState<PublicProfileListItem[]>([]);
  // Jawny sygnał „klient odpowiedział", bo nie da się go wywnioskować z danych:
  // `isFetched` jest prawdziwe także po błędzie, a niepusty alfabet nie zachodzi dla
  // typu bez ani jednego profilu — /kolekcjonerzy dostaje z API puste `pagination`,
  // więc bramka oparta na alfabecie byłaby tam otwarta na stałe.
  const [hasClientData, setHasClientData]: UseState<boolean> = useState<boolean>(false);

  const { isLoading }: UseQueryResult = useQuery(
    [QueryKey.PublicProfilesList, activeLetter],
    (): Promise<PublicProfileList> => getPublicProfilesList(type, activeLetter),
    {
      onSuccess: (data: PublicProfileList): void => {
        setAlphabet(data.pagination);
        setProfilesList(data.elements);
        setHasClientData(true);
      },
      onError: (error: FrontendApiError): void => logger(QueryKey.PublicProfilesList, error)
    }
  );

  /**
   * Serwer wypełnia pamięć podręczną pod stabilnym kluczem (ssrPrefetch.helper.ts).
   * Ten hook trzyma listę w stanie lokalnym wypełnianym w `onSuccess`, a `onSuccess`
   * przy renderowaniu serwerowym się nie wykonuje — bez tej ścieżki listing wychodził
   * z SSR pusty. Dane służą wyłącznie pierwszemu renderowaniu.
   *
   * Odcinamy je dopiero, gdy klient FAKTYCZNIE odpowiedział — patrz `hasClientData`
   * wyżej. Nie gdy zapytanie się zakończyło (`isFetched` jest prawdziwe także po
   * błędzie) i nie na podstawie samych danych (pusty typ nie odróżniłby się od braku
   * odpowiedzi). Dzięki temu udana pusta odpowiedź normalnie zastępuje dane z serwera,
   * a nieudana ich nie kasuje.
   *
   * `isLoading` gasimy razem z tymi danymi, bo widoki mają warunek `!isLoading && …`:
   * na serwerze zapytanie startuje i nikt na nie nie czeka, więc bez tego lista
   * wypadałaby z HTML-a mimo wypełnionej pamięci podręcznej.
   */
  const queryClient: QueryClient = useQueryClient();
  const ssrList: PublicProfileList | undefined = hasClientData
    ? undefined
    : queryClient.getQueryData<PublicProfileList>(ssrQueryKey(`${QueryKey.PublicProfilesList}-${type}-${activeLetter}`));

  const visibleAlphabet: Record<string, number> = hasClientData ? alphabet : (ssrList?.pagination ?? {});

  const initialActiveLetter: string = useMemo(
    (): string => {
      return Object.keys(visibleAlphabet)[0] ?? '';
    },
    [visibleAlphabet]
  );

  return {
    alphabet: visibleAlphabet,
    activeLetter,
    setActiveLetter,
    isLoading: isLoading && !ssrList,
    profilesList: hasClientData ? profilesList : (ssrList?.elements ?? []),
    initialActiveLetter
  };
};
